/**
 * @file fileStorageHelper.ts
 * @description Centralized S3 file storage utilities.
 *
 * Rule: NEVER store full S3 URLs. Store only fileKey.
 * Construct display URLs dynamically using the configured base URL.
 */

import apiClient from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';

// ── Config ─────────────────────────────────────────────────────────────────

const FILE_BASE_URL = import.meta.env.VITE_FILE_BASE_URL ?? '';

// ── Types ──────────────────────────────────────────────────────────────────

export interface FileUploadResult {
  fileKey: string;
  fileName: string;
  fileSize: number;
}

// ── URL builder ────────────────────────────────────────────────────────────

/**
 * Build a displayable URL from a file key.
 * Falls back to the secure-file proxy when no public base URL is configured.
 */
export function getFileUrl(fileKey: string | null | undefined): string {
  if (!fileKey) return '';
  if (FILE_BASE_URL) {
    return `${FILE_BASE_URL.replace(/\/+$/, '')}/${fileKey}`;
  }
  // Fallback: proxy through the secure-file endpoint
  return `/api/shared/secure-file/${encodeURIComponent(fileKey)}`;
}

// ── Upload ─────────────────────────────────────────────────────────────────

/**
 * Upload a file to S3 via the backend.
 * Returns only the fileKey — never a full URL.
 */
export async function uploadFile(
  file: File,
  folder: string = 'form-uploads',
): Promise<FileUploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const response = await apiClient.post<ApiResponse<FileUploadResult>>(
    '/shared/files/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );

  const result = response.data?.data;
  if (!result?.fileKey) {
    throw new Error('File upload failed: no fileKey returned.');
  }
  return result;
}

// ── Validation helpers ─────────────────────────────────────────────────────

const DEFAULT_IMAGE_FORMATS = ['png', 'jpg', 'jpeg', 'webp'];

export function isAllowedImageFormat(
  file: File,
  allowed: string[] = DEFAULT_IMAGE_FORMATS,
): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return allowed.includes(ext);
}

export function isWithinSizeLimit(file: File, maxSizeMB: number = 5): boolean {
  return file.size <= maxSizeMB * 1024 * 1024;
}
